/* eslint-disable no-restricted-syntax */
import { useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import {
  Currency,
  HostDashboardBookingsDocument,
  HostDashboardBookingsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Papa from 'papaparse';
import { Alert, Button, Table } from 'react-bootstrap';
import LoadingPage from '../../utilities/LoadingPage';
import PriceFormat from '../../utilities/PriceFormat';

/**
 * Count the Group's active booking spaces
 *
 * @param group Group from the query
 */
export function countGroupActiveSpacesBooked(group: {
  bookings?: {
    items?: Array<{ spaces: Array<{ active: boolean }> }>;
  };
}): number {
  if (!group.bookings?.items) return 0;
  return group.bookings.items.reduce(
    (acc, booking) => acc + booking.spaces.filter((i) => i.active).length,
    0,
  );
}

/**
 * Create a list of groups
 *
 * We want a list of groups in the format of the csv download, that can then be sorted and passed around components
 * easily.
 *
 * @param data Graphql query response
 */
export function createGroupsList(
  data: HostDashboardBookingsQuery,
): HostDashboardGroup[] {
  const groups: HostDashboardGroup[] = [];

  for (const trip of data!.hostByURL.trips.items!) {
    for (const group of trip.groups.items!) {
      const hostDashboardGroup: HostDashboardGroup = {
        tripTitle: trip.title,
        startLocalTime: DateTime.fromISO(group.start.utc, {
          zone: group.start.zone,
        }).toISO(),
        monthName: DateTime.fromISO(group.start.utc, {
          zone: group.start.zone,
        }).toFormat('LLLL yyyy'), // E.g. January 2030
        endLocalTime: DateTime.fromISO(group.end.utc, {
          zone: group.end.zone,
        }).toISO(),
        spacesBooked: countGroupActiveSpacesBooked(group),
        spacesRemaining: group.remainingSpaces,
        hostPrice: group.hostPrice.amountZD,
        currency: group.hostPrice.currency,
        private: group.private,
        groupID: group.id,
        tripID: trip.id,
      };
      groups.push(hostDashboardGroup);
    }
  }

  return groups;
}

/**
 * Sum the Host's total bookings (of all time)
 *
 * Note this is approximate as it used the current trip price, not the trip price for each booking.
 */
export function sumTotalBookings(groups: HostDashboardGroup[]) {
  const spaces = groups.reduce((prev, curr) => curr.spacesBooked + prev, 0);
  const amountZD = groups.reduce(
    (prev, curr) => curr.spacesBooked * curr.hostPrice + prev,
    0,
  );

  return {
    spaces,
    amountZD,
    currency: groups[0]?.currency,
  };
}

/**
 * FIlter bookings by date
 *
 * For use with Array.filter
 */
export function filterGroupDate(
  group: HostDashboardGroup,
  endDateFilter?: string,
): boolean {
  const groupStart = DateTime.fromISO(group.startLocalTime);
  const endFilter = DateTime.fromISO(endDateFilter);
  if (endDateFilter && groupStart > endFilter) return false;
  return true;
}

/**
 * Sort groups by date
 *
 * For use with `Array.sort()`
 */
export function sortGroupDateAscending(
  a: HostDashboardGroup,
  b: HostDashboardGroup,
) {
  const aDate = DateTime.fromISO(a.startLocalTime);
  const bDate = DateTime.fromISO(b.startLocalTime);
  if (bDate > aDate) return -1;
  if (aDate > bDate) return 1;
  return 0;
}

/**
 * Splits groups by month
 *
 * @example
 * // Result
 * {
 *   'January 2020': [
 *     {
 *       id: "group_uuid"
 *     }
 *   ]
 * }
 *
 * Note JavaScript guarantees the order of insertion in an object is kept, so we don't need to worry about re-ordering
 * these again.
 *
 * @params groups Date-sorted list of groups
 */
export function splitGroupsByMonth(groups: HostDashboardGroup[]): {
  [key: string]: HostDashboardGroup[];
} {
  const months = {};
  groups.forEach((group) => {
    if (!months[group.monthName]) months[group.monthName] = [];
    months[group.monthName].push(group);
  });
  return months;
}

/**
 * Group table row
 */
export function GroupRow({
  group,
}: {
  group: Pick<
    HostDashboardGroup,
    | 'groupID'
    | 'private'
    | 'spacesBooked'
    | 'spacesRemaining'
    | 'startLocalTime'
    | 'tripTitle'
  >;
}) {
  const router = useRouter();

  return (
    <Link
      href={`${router.asPath}/${group.groupID}`}
      key={group.groupID}
      legacyBehavior
    >
      <tr className="bg-primary text-white">
        <td style={{ cursor: 'pointer' }}>
          {DateTime.fromISO(group.startLocalTime, { setZone: true }).toFormat(
            'ccc d LLL',
          )}
          {group.private && (
            <>
              {' '}
              <em>(Private)</em>
            </>
          )}
        </td>
        <td style={{ cursor: 'pointer' }}>{group.tripTitle}</td>
        <td style={{ cursor: 'pointer' }}>
          {group.spacesBooked} /{group.spacesBooked + group.spacesRemaining}
        </td>
      </tr>
    </Link>
  );
}

/**
 * Month Section
 */
export function Month({
  monthName,
  groups,
}: {
  monthName: string;
  groups: HostDashboardGroup[];
}) {
  return (
    <>
      <h2>{monthName}</h2>
      <Table responsive>
        <thead>
          <tr>
            <td>Date</td>
            <td>Trip Name</td>
            <td>Spaces</td>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <GroupRow group={group} key={group.groupID} />
          ))}
        </tbody>
      </Table>
    </>
  );
}

/**
 * Download CSV button
 */
function Download({ groups }: { groups: HostDashboardGroup[] }) {
  function downloadCsv() {
    const csv = Papa.unparse(groups);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    if ((window.navigator as any).msSaveOrOpenBlob)
      // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
      (window.navigator as any).msSaveBlob(blob, 'bookings.csv');
    else {
      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = 'bookings.csv';
      a.type = 'text/csv';
      document.body.appendChild(a);
      a.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
      document.body.removeChild(a);
    }
  }

  return (
    <div className="d-grid gap-2">
      <Button variant="success" onClick={downloadCsv}>
        Download CSV
      </Button>
    </div>
  );
}

/**
 * Show a list of all the host's groups, with their booking numbers
 */
export default function BookingsList({
  hostURL,
  startDateFilter,
  endDateFilter,
}: {
  hostURL: string;
  startDateFilter: string;
  endDateFilter: string;
}) {
  // Get the data
  const { data, loading, error } = useQuery(HostDashboardBookingsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      urlSlug: hostURL,
      startDateFrom: DateTime.fromISO(startDateFilter).toISO(),
      startDateTo: DateTime.fromISO(endDateFilter).toISO(),
    },
  });

  // Handle loading error
  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage error={error} />;

  // Get the groups in date order
  const groups = createGroupsList(data);
  const groupsFiltered = groups.filter((group) =>
    filterGroupDate(group, endDateFilter),
  );
  const groupsSorted = groupsFiltered.sort(sortGroupDateAscending);
  const groupsSplitByMonth = splitGroupsByMonth(groupsSorted);

  // Get the total of all bookings
  const totalAllBookings = sumTotalBookings(groups);

  // Setup the months
  const months = Object.keys(groupsSplitByMonth).map((monthName) => (
    <Month
      monthName={monthName}
      groups={groupsSplitByMonth[monthName]}
      key={monthName}
    />
  ));

  return (
    <>
      {totalAllBookings.spaces > 0 && (
        <Alert variant="success">
          Great work! You have{' '}
          <strong>
            {new Intl.NumberFormat().format(totalAllBookings.spaces)}
          </strong>{' '}
          bookings from Skyhook below, totalling around{' '}
          <strong>
            <PriceFormat
              amount={totalAllBookings.amountZD}
              currencyIso={totalAllBookings.currency}
              short
              roundDollar
            />
          </strong>{' '}
          of revenue for you.
        </Alert>
      )}
      <Download groups={groups} />
      {months}
    </>
  );
}

/**
 * Host Dashboard Group
 *
 * Used for the CSV download, and also to pass around data between components;
 */
export interface HostDashboardGroup {
  tripTitle: string;
  startLocalTime: string;
  monthName: string;
  endLocalTime: string;
  spacesBooked: number;
  spacesRemaining: number;
  hostPrice: number;
  currency: Currency;
  private: boolean;
  groupID: string;
  tripID: string;
}
